import type { PackagingPlanRow } from "./packaging-report-types.js";
import type { QuantityResult } from "./packaging-quantities.js";
import { isPackagingWorkOrderPending } from "./packaging-work-order.js";
export type ReportQuantityRow = { plan: PackagingPlanRow; weekPlan: QuantityResult; weeklyActual: QuantityResult; remaining: QuantityResult; sharedRows: number };
export type ReportMetric = { value: number | null; knownValue: number; unknownCount: number; count: number };
export type ReportSummary = {
  rowCount: number; plan: ReportMetric; actual: ReportMetric; remaining: ReportMetric; excess: ReportMetric;
  metCount: number; remainingCount: number; unknownCount: number; sharedCount: number; notStartedCount: number;
  coverage: number | null; verifiedCoverage: number | null; verifiedCount: number;
};
export const isKnownQuantity = (value: number | null | undefined): value is number => typeof value === "number" && Number.isFinite(value);
const quantity = (value: number | null, reason: string | null = null): QuantityResult => ({ value, unit: value === null ? null : "KOLİ", reason });
export function sumReportMetric(results: QuantityResult[]): ReportMetric {
  const known = results.filter((item) => isKnownQuantity(item.value));
  const knownValue = known.reduce((sum, item) => sum + (item.value ?? 0), 0);
  const unknownCount = results.length - known.length;
  // No records is not a completed zero-production plan. Partial totals are NEVER full totals.
  return { value: !results.length || unknownCount ? null : knownValue, knownValue, unknownCount, count: results.length };
}
export function reportRowStatus(row: ReportQuantityRow): { label: string; tone: string } {
  if (isPackagingWorkOrderPending(row.plan)) return { label: "İş emri bekleniyor", tone: "pending" };
  if (row.plan.unplanned) return { label: "Günlük planla eşleşmedi", tone: row.weeklyActual.value === null ? "unknown" : "neutral" };
  if (!isKnownQuantity(row.weekPlan.value) || !isKnownQuantity(row.weeklyActual.value) || !isKnownQuantity(row.remaining.value)) return { label: "Doğrulanamadı", tone: "unknown" };
  if (row.weekPlan.value <= 0) return { label: "Plan yok", tone: "neutral" };
  if (row.remaining.value <= 0) return { label: "Hafta planı karşılandı", tone: "met" };
  return { label: row.weeklyActual.value <= 0 ? "Üretim bekliyor" : "Kısmen üretildi", tone: "remaining" };
}
export function rowExcess(row: ReportQuantityRow): number | null {
  if (row.plan.unplanned) return 0;
  const actual = row.weeklyActual;
  return isKnownQuantity(row.weekPlan.value) && isKnownQuantity(actual.value)
    ? Math.max(0, actual.value - row.weekPlan.value) : null;
}
export function summarizeReportRows(rows: ReportQuantityRow[]): ReportSummary {
  const plan = sumReportMetric(rows.filter((r) => !r.plan.unplanned).map((r) => r.weekPlan));
  // Pending Excel targets belong to the plan, but supply no verified production.
  const productionRows = rows.filter((r) => !isPackagingWorkOrderPending(r.plan));
  const actual = sumReportMetric(productionRows.map((r) => r.weeklyActual));
  const remaining = sumReportMetric(rows.filter((r) => !r.plan.unplanned)
    .map((r) => isPackagingWorkOrderPending(r.plan) ? r.weekPlan : r.remaining));
  const excess = sumReportMetric(productionRows.map((r) => quantity(rowExcess(r))));
  // Cap each row independently: extra units on A must never hide missing units on B.
  const coverage = plan.value !== null && plan.value > 0 && remaining.value !== null
    ? Math.round(Math.max(0, Math.min(1, (plan.value - remaining.value) / plan.value)) * 100) : null;
  const verifiedRows = rows.filter((row) => isKnownQuantity(row.weekPlan.value)
    && isKnownQuantity(row.weeklyActual.value) && isKnownQuantity(row.remaining.value));
  const verifiedPlan = verifiedRows.reduce((sum, row) => sum + (row.weekPlan.value ?? 0), 0);
  const verifiedRemaining = verifiedRows.reduce((sum, row) => sum + (row.remaining.value ?? 0), 0);
  const verifiedCoverage = verifiedPlan > 0
    ? Math.round(Math.max(0, Math.min(1, (verifiedPlan - verifiedRemaining) / verifiedPlan)) * 100) : null;
  const unknownCount = rows.filter((row) => {
    const tone = reportRowStatus(row).tone;
    return tone === "unknown" || tone === "pending";
  }).length;
  return {
    rowCount: rows.length, plan, actual, remaining, excess, coverage, verifiedCoverage, unknownCount,
    verifiedCount: verifiedRows.length,
    metCount: rows.filter((r) => reportRowStatus(r).tone === "met").length,
    remainingCount: rows.filter((r) => reportRowStatus(r).tone === "remaining").length,
    notStartedCount: rows.filter((r) => r.weeklyActual.value === 0 && (r.weekPlan.value ?? 0) > 0).length,
    sharedCount: rows.filter((r) => r.sharedRows > 1).length,
  };
}
